import type { JSX } from 'react';
import { SessionExpiryBanner } from '@components/session-expiry-banner';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import EditModeAlertBanner from '@routes/profile-bar/___shared___/components/edit-mode-alert-banner';
import { QuestionsCsvImportFailedBanner } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/questions/ui/banners/import-failed';
import { QuestionsCsvImportWithErrorsBanner } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/questions/ui/banners/import-with-errors';
import { TaskCsvImportFailedBanner } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/banners/csv-import-failed';
import { TaskCsvImportWithErrorsBanner } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/banners/csv-import-with-errros';
import { CSVImportFailedBanner as CSVImportFailedBannerCrewUsers } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/ui/banners/import-failed';
import { CSVImportWithErrorsBanner as CSVImportWithErrorsBannerCrewUsers } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/ui/banners/import-with-errors';
import { CSVImportFailedBanner as CSVImportFailedBannerShoreUsers } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/ui/banners/import-failed';
import { CSVImportWithErrorsBanner as CSVImportWithErrorsBannerShoreUsers } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/ui/banners/import-with-errors';
import { CSVImportFailedBanner as CSVImportFailedBannerVessels } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/vessels/ui/banners/import-failed';
import { CSVImportWithErrorsBanner as CSVImportWithErrorsBannerVessels } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/vessels/ui/banners/import-with-errors';
import { SubscriptionDeactivationErrorBanner } from '@routes/sidebar/admin-center/tenant-client/___pages___/subscriptions/ui/table/subscription-deactivation-error';
import type { RootState } from '@store/store.ts';

const useCsvImportState = (selector: (state: RootState) => any) => {
    const { csvUploadState, isCsvImportErroneous, isCsvImportFailed, uploadedCsvFilename } = useAppSelector(selector);

    const totalRows = csvUploadState?.totalRows ?? 0;
    const successfullyProcessedRows = csvUploadState?.successfullyProcessedRows ?? 0;

    return {
        isCsvImportErroneous,
        isCsvImportFailed,
        uploadedCsvFilename,
        totalRows,
        successfullyProcessedRows,
    };
};

export const useRenderCustomBanner = () => {
    const isEditingProfile = useAppSelector((state) => state.myProfile.isEditingProfile);
    const showDeactivateSubscriptionErrorBanner = useAppSelector(
        (state) => state.adminCenterSubscriptions.showDeactivateSubscriptionErrorBanner,
    );

    const shoreUsers = useCsvImportState((state) => state.adminCenterShoreUsers);
    const vessels = useCsvImportState((state) => state.adminCenterVessels);
    const crewUsers = useCsvImportState((state) => state.adminCenterCrewUsers);

    const isSessionExpiringSoon = useAppSelector((state) => state.auth.isSessionExpiringSoon);

    const {
        isQuestionsCsvImportErroneous,
        isQuestionsCsvImportFailed,
        uploadedQuestionsCsvFilename,
        questionsCsvImportRejectedCount,
        questionsCsvImportSuccessCount,
        isTaskCsvImportErroneous,
        isTaskCsvImportFailed,
        uploadedTaskCsvFilename,
        taskCsvImportRejectedCount,
        taskCsvImportSuccessCount,
    } = useAppSelector((state) => state.adminCenterCreatorElements);

    const bannerStrategies: {
        condition: () => boolean;
        component: JSX.Element;
        action?: () => void;
    }[] = [
        {
            condition: () => isSessionExpiringSoon,
            component: <SessionExpiryBanner />,
        },
        {
            condition: () => isEditingProfile,
            component: <EditModeAlertBanner />,
        },
        {
            condition: () => shoreUsers.isCsvImportErroneous,
            component: (
                <CSVImportWithErrorsBannerShoreUsers
                    filename={shoreUsers.uploadedCsvFilename}
                    recordsUploaded={shoreUsers.successfullyProcessedRows}
                    recordsRejected={shoreUsers.totalRows - shoreUsers.successfullyProcessedRows}
                />
            ),
        },
        {
            condition: () => shoreUsers.isCsvImportFailed,
            component: <CSVImportFailedBannerShoreUsers filename={shoreUsers.uploadedCsvFilename} />,
        },
        {
            condition: () => vessels.isCsvImportErroneous,
            component: (
                <CSVImportWithErrorsBannerVessels
                    filename={vessels.uploadedCsvFilename}
                    recordsUploaded={vessels.successfullyProcessedRows}
                    recordsRejected={vessels.totalRows - vessels.successfullyProcessedRows}
                />
            ),
        },
        {
            condition: () => vessels.isCsvImportFailed,
            component: <CSVImportFailedBannerVessels filename={vessels.uploadedCsvFilename} />,
        },
        {
            condition: () => crewUsers.isCsvImportErroneous,
            component: (
                <CSVImportWithErrorsBannerCrewUsers
                    filename={crewUsers.uploadedCsvFilename}
                    recordsUploaded={crewUsers.successfullyProcessedRows}
                    recordsRejected={crewUsers.totalRows - crewUsers.successfullyProcessedRows}
                />
            ),
        },
        {
            condition: () => crewUsers.isCsvImportFailed,
            component: <CSVImportFailedBannerCrewUsers filename={crewUsers.uploadedCsvFilename} />,
        },
        {
            condition: () => isQuestionsCsvImportErroneous,
            component: (
                <QuestionsCsvImportWithErrorsBanner
                    filename={uploadedQuestionsCsvFilename}
                    recordsUploaded={questionsCsvImportSuccessCount}
                    recordsRejected={questionsCsvImportRejectedCount}
                />
            ),
        },
        {
            condition: () => isQuestionsCsvImportFailed,
            component: <QuestionsCsvImportFailedBanner filename={uploadedQuestionsCsvFilename} />,
        },
        {
            condition: () => showDeactivateSubscriptionErrorBanner,
            component: <SubscriptionDeactivationErrorBanner />,
        },
        {
            condition: () => isTaskCsvImportErroneous,
            component: (
                <TaskCsvImportWithErrorsBanner
                    filename={uploadedTaskCsvFilename}
                    recordsUploaded={taskCsvImportSuccessCount}
                    recordsRejected={taskCsvImportRejectedCount}
                />
            ),
        },
        {
            condition: () => isTaskCsvImportFailed,
            component: <TaskCsvImportFailedBanner filename={uploadedTaskCsvFilename} />,
        },
    ];

    const match = bannerStrategies.find(({ condition }) => condition());

    return match ? match.component : null;
};
